import {createSlice, current} from '@reduxjs/toolkit'
import {WEBSITE_DESIGN_FEE_ENTITLEMENT} from '../components/keap-partner/kp-11-link/kp-11Link-utility/kp11-utility'
import {fetchPlanIcon} from '../utils/fetch-plan-icon'
import {
  CC_CURRENCIES,
  COUNTRIES,
  KEAP_ADDONS_PARENT_CODES,
  KEAP_ADDONS_PARENT_NON_LEGACY_CODES,
  KEAP_FAMILY_CODES,
  KEAP_TIER_CODES,
  MC_2_0_PLANS,
} from '../utils/constants'
import {resolveApplicableFees} from '../utils/feeUtils'

// MC tier order for downgrade check (API often has productRank: null)
const MC_TIER_CODES = [
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'mc_pro_enh-5-thryv-m2m',
]

const CONVRRT_LP_PRO_NAME = 'convrrt landing page pro'
const CONVRRT_LP_LITE_NAME = 'convrrt landing page lite'
const KEAP_TEXT_VOICE_STANDALONE_CODES = new Set([
  'addon-5-keap-textvoice-m2m',
  'addon-5-keap-textvoice-annual',
])
const KEAP_INCLUDED_TEXT_VOICE_ENTITLEMENT_CODES = new Set([
  'ent-keap-included-txtvoice',
  'ent-keap-included-txtvoice-annual',
])

const getProductIdentity = product => {
  const code = String(product?.code || '')
    .toLowerCase()
    .trim()
  const name = normalizeProductName(product?.name)
  return code || name
}

const mergeUniqueProducts = (items = [], additions = []) => {
  const existingKeys = new Set(items.map(getProductIdentity).filter(Boolean))
  const next = [...items]
  additions.forEach(product => {
    const key = getProductIdentity(product)
    if (!key || existingKeys.has(key)) return
    existingKeys.add(key)
    next.push(product)
  })
  return next
}

const normalizeProductName = name => (name || '').toLowerCase().trim()

const isConvrrtLPPro = name =>
  normalizeProductName(name).includes(CONVRRT_LP_PRO_NAME)

const isConvrrtLPLite = name =>
  normalizeProductName(name).includes(CONVRRT_LP_LITE_NAME)

const KEAP_CORE_PRODUCT_NAME_MATCHERS = [
  'keap lite',
  'keap max',
  'keap pro',
  'keap ultimate',
]

const isKeapCoreProduct = product => {
  // Addon parent containers are never core keap products — they must not be
  // included in the mutual-exclusion set or treated as replaceable keap plans.
  if (KEAP_ADDONS_PARENT_CODES.includes(product?.code)) return false
  const normalizedName = normalizeProductName(product?.name)
  return (
    KEAP_TIER_CODES.includes(product?.code) ||
    KEAP_FAMILY_CODES.includes(product?.code) ||
    KEAP_CORE_PRODUCT_NAME_MATCHERS.some(keyword =>
      normalizedName.includes(keyword),
    )
  )
}

const getDefaultCentersAndApps = () => ({
  centers: [
    {
      code: 'cc_basicpls-5-thryv-m2m',
      name: 'My Account',
      id: 'cc_basicpls-5-thryv-m2m',
      img: null, // Will be set by component
      groupTitle: 'Command Center',
      entitlements: [],
      amount: 0,
      uuid: '',
      unitAmount: 0,
      quantity: 1,
      addons: [],
    },
  ],
  apps: [
    {
      id: 'xt2e88macu3g',
      uuid: '',
      state: 'active',
      code: 'appmarket-5-thryv-m2m',
      name: 'Thryv Add-Ons',
      groupTitle: 'Thryv Add-Ons',
      quantity: 1,
      unitAmount: 0,
      alphaDomain: null,
      alphaProposalId: null,
      img: null,
      subtotal: 0,
      total: 0,
      addons: [
        {
          id: 'xt2e88qjwgey',
          subscriptionId: '',
          code: 'addon-5-signatures_usage',
          name: 'Signatures Usage',
          quantity: 1,
          subtotal: null,
          groupTitle: 'Thryv Add-Ons',
          img: null,
          amount: 0,
          unitAmount: 0,
        },
        {
          id: 'xt2e88qlqqcs',
          subscriptionId: '',
          code: 'app-websitebuilder',
          name: 'Website Builder',
          img: null,
          quantity: 1,
          unitAmount: 0,
          subtotal: 0,
          groupTitle: 'Thryv Add-Ons',
          amount: 0,
        },
      ],
      amount: 0,
    },
  ],
  oneTimeFees: [],
})

const DEFAULT_CENTERS_AND_APPS = getDefaultCentersAndApps()

const initialState = {
  catalogGroups: [],
  allGroupCustomFieldsMap: {},
  centersNAppVList: {},
  defaultAllGroupCustomFieldsMap: {},
  defaultCartCentersNApps: DEFAULT_CENTERS_AND_APPS,
  selectCartCentersNApps: DEFAULT_CENTERS_AND_APPS,
  thryvSubscriptionResponse: null,
  keapTextVoiceQuantity: 0,
  selectedKeapTextVoiceOption: '',
  selectedKeapKcpOption: '',
  selectedKeapAddContactsOption: '',
  keapAddContactsQuantity: 0,
  keapAddUsersQuantity: 0,
  selectedKeapLegacyGraphlyContactsOption: '',
  keapLegacyGraphlyContactsQuantity: 0,
  selectedKeapLegacyPlusThisPremiumOption: '',
  keapLegacyPlusThisPremiumQuantity: 0,
  alphaSEOFromRecurly: {},
  socialAdsBronzeQuantity: 0,
  socialAdsSilverQuantity: 0,
  socialAdsGoldQuantity: 0,
  alphaPowerboostCount: 0,
  alphaKeyCount: 0,
  selectedCountry: 'US',
  catalogCardCollapseStatus: {
    'thryv leads': false,
    seo: false,
    'social ads': false,
  },
  parentCardCollapseStatus: {
    marketingcenter: true,
    workforcecenter: true,
    'thryvadd-ons': true,
    keap: true,
    businesscenter: true,
    'keapadd-ons': true,
    partnercertification: true,
    adsboost: true,
    brandboost: true,
    socialboost: true,
    searchboost: true,
  },
  cartItems: {},
  collapseAllTrigger: 0,
  disabledItems: {},
  isDataLoaded: false,
}

// Auto-added items configuration
// const autoAddedItemsByGroupKey = {
//   marketingCenter: [{code: 'rc-5-thryv-m2m', section: 'centers'}],
// }

// ============== Helper Functions ==============
// const updateGroupItems = (groups, predicate, updater) =>
//   groups.map(g => (predicate(g) ? updater(g) : g))

const fetchDependentProducts = (
  newlyAddedProductsArg,
  allProductsArg,
  removedProduct,
  dependentProds = [],
) => {
  // Loop through all newly added products and remove the ones
  // that are dependent on the item being removed
  newlyAddedProductsArg.forEach(newProd => {
    let newProductRequirements
    const normalizedRequires =
      newProd?.configuration?.requires?.length > 0
        ? newProd.configuration.requires
        : newProd?.configuration?.requiresCenter || []
    if (normalizedRequires && normalizedRequires.length > 0)
      newProductRequirements = [...normalizedRequires]
    if (
      newProd?.configuration?.requiresCenter &&
      newProd?.configuration?.requiresCenter.length > 0
    )
      newProductRequirements = [...newProd.configuration.requiresCenter]
    if (
      newProd?.configuration?.requiresAddon &&
      newProd?.configuration?.requiresAddon.length > 0
    )
      newProductRequirements = [...newProd.configuration.requiresAddon]
    if (
      newProd?.configuration?.appliesTo &&
      newProd?.configuration?.appliesTo.length > 0
    )
      newProductRequirements = [...newProd.configuration.appliesTo]
    if (newProductRequirements && newProductRequirements.length > 0) {
      if (
        !newProductRequirements.find(prod => {
          return (
            prod !== removedProduct?.code &&
            allProductsArg.map(item => item.code).includes(prod)
          )
        })
      ) {
        allProductsArg = allProductsArg.filter(
          item => item.code !== newProd.code,
        )
        dependentProds.push(newProd)
        fetchDependentProducts(
          newlyAddedProductsArg.filter(item => item.code !== newProd.code),
          allProductsArg.filter(item => item.code !== newProd.code),
          newProd,
        )
      }
    }
  })
}

// Helper function to check if item should be disabled
const checkIfItemDisabled = (
  item,
  selectedCodes,
  selectCartCentersNApps,
  centersNAppVList,
  defaultCartCentersNApps,
) => {
  // Don't disable if already selected
  if (selectedCodes.includes(item.code)) {
    return false
  }

  // Disable the standalone website addon when:
  //  (a) ent-5-website is already bundled with any cart center — covers mc_pro
  //      (entitlements) and Signature Plus with website checked (addons).
  //  (b) the user has access to any Signature-family plan in the catalog
  //      (centersNAppVList) — website is already included in those plans so
  //      the standalone addon should never be selectable.
  if (item.code === 'addon-5-website_difm') {
    const SIGNATURE_FAMILY_CODES = [
      'signature_plus-5-thryv-m2m',
      'signature_plus-5-thryv-annual',
      'signature-5-thryv-annual',
      'mc_prov2-5-thryv-m2m',
    ]
    const hasWebsiteEnt = (selectCartCentersNApps?.centers ?? []).some(
      cartItem =>
        (cartItem.addons ?? []).some(a => a.code === 'ent-5-website') ||
        (cartItem.entitlements ?? []).some(e => e.code === 'ent-5-website'),
    )
    const hasSignatureFamilyAccess = (centersNAppVList?.centers ?? []).some(c =>
      SIGNATURE_FAMILY_CODES.includes(c.code),
    )
    if (hasWebsiteEnt || hasSignatureFamilyAccess) return true
  }

  if (
    item.code.includes('keap') &&
    selectedCodes.some(code => code.includes('keap'))
  ) {
    return false
  }

  // Special case: TL product require MC
  if (item.code === 'tl-mc-5-thryv-m2m') {
    const hasMCPackage = selectedCodes.some(
      code => code === 'mc_plus-5-thryv-m2m' || code === 'mc_pro-5-thryv-m2m',
    )
    return !hasMCPackage
  }

  // Special case: if addon-5-mcstartgr is selected, exclude addon-5-mcexpangr since they can't coexist, but not vice versa since selecting expanded doesn't exclude start
  if (item.code === 'addon-5-mcstartgr') {
    const hasExpandedGP = selectedCodes.some(
      code => code === 'addon-5-mcexpangr',
    )
    const hasTL = selectedCodes.some(code =>
      ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
        code,
      ),
    )

    if (hasExpandedGP || hasTL) {
      return true
    }
  }

  if (item.code === 'addon-5-mcexpangr') {
    const hasTL = selectedCodes.some(code =>
      ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
        code,
      ),
    )
    if (hasTL) {
      return true
    }
  }

  // Convrrt Landing Page Pro/Lite are mutually exclusive.
  const allCatalogProducts = [
    ...(centersNAppVList.centers || []),
    ...(centersNAppVList.apps || []),
    ...(centersNAppVList.oneTimeFees || []),
  ]
  const selectedProducts = selectedCodes
    .map(code => allCatalogProducts.find(p => p.code === code))
    .filter(Boolean)

  if (isConvrrtLPPro(item?.name)) {
    const hasLiteSelected = selectedProducts.some(product =>
      isConvrrtLPLite(product?.name),
    )
    if (hasLiteSelected) {
      return true
    }
  }

  if (isConvrrtLPLite(item?.name)) {
    const hasProSelected = selectedProducts.some(product =>
      isConvrrtLPPro(product?.name),
    )
    if (hasProSelected) {
      return true
    }
  }

  // Cross-generation exclusion: MC 1.0 (marketingCenter) vs MC 2.0 (thryvgrowthplatform).
  // Rule: the two families cannot coexist in the same new order.
  // Exception: if MC 1.0 is already on the account (isAlreadyAdded), MC 2.0 can still be
  // added (upgrade path). The reverse is not allowed — MC 2.0 already on account blocks MC 1.0.
  const isMC10Item = MC_TIER_CODES.includes(item.code)
  const isMC20Item = MC_2_0_PLANS.includes(item.code)
  if (isMC10Item || isMC20Item) {
    const cartCenters = selectCartCentersNApps.centers || []
    if (isMC20Item) {
      // Disable MC 2.0 only when MC 1.0 is being *newly* added in this order.
      // Pre-existing MC 1.0 (isAlreadyAdded) is the upgrade scenario and must stay allowed.
      const hasNewMC10InCart = cartCenters.some(
        c => MC_TIER_CODES.includes(c.code) && !c.isAlreadyAdded,
      )
      if (hasNewMC10InCart) return true
    }
    if (isMC10Item) {
      // Disable MC 1.0 whenever any MC 2.0 plan is in the cart, whether newly added
      // or already on the account — mixing the two generations is never valid for MC 1.0.
      const hasMC20InCart = cartCenters.some(c => MC_2_0_PLANS.includes(c.code))
      if (hasMC20InCart) return true
    }
  }

  // Don't disable if already in default cart (existing subscription)
  // const isInDefaultCart = [
  //   ...(defaultCartCentersNApps.centers || []),
  //   ...(defaultCartCentersNApps.apps || []),
  //   ...(defaultCartCentersNApps.oneTimeFees || []),
  // ].some(defaultItem => defaultItem.code === item.code)

  // if (isInDefaultCart) {
  //   return false
  // }

  // Check if item is excluded by any selected item
  const isExcludedBySelected = selectedCodes.some(selectedCode => {
    const selectedItem = [
      ...(centersNAppVList.centers || []),
      ...(centersNAppVList.apps || []),
      ...(centersNAppVList.oneTimeFees || []),
    ].find(i => i.code === selectedCode)
    // Check if item would be excluded
    const wouldBeExcluded = selectedItem?.excludes?.includes(item.code)

    if (!wouldBeExcluded) {
      return false
    }

    return true // Item is excluded
  })
  if (isExcludedBySelected) return true

  // Check if item excludes any selected item
  if (item.excludes && item.excludes.length > 0) {
    const excludesSelected = item.excludes.some(excludedCode =>
      selectedCodes.includes(excludedCode),
    )
    if (excludesSelected) return true
  }

  // Center downgrade: disable if selecting a lower tier when a higher tier is in cart
  const allProducts = [
    ...(centersNAppVList.centers || []),
    ...(centersNAppVList.apps || []),
    ...(centersNAppVList.oneTimeFees || []),
  ]
  const selectedCenters = selectCartCentersNApps.centers || []
  const getProductRank = (prod, uigroup) => {
    if (prod?.productRank != null) return prod.productRank
    const uig = (uigroup || prod?.uigroup || prod?.groupKey || '')
      .toLowerCase()
      .replace(/\s/g, '')
    if (uig === 'keap') {
      const idx = KEAP_TIER_CODES.indexOf(prod?.code)
      return idx >= 0 ? idx : -1
    }
    if (uig === 'marketingcenter') {
      const idx = MC_TIER_CODES.indexOf(prod?.code)
      return idx >= 0 ? idx : -1
    }
    return -1
  }
  const isSameCenterFamily = (a, b) => {
    const norm = uig => (uig || '').toLowerCase().replace(/\s/g, '')
    const uigA = norm(a?.uigroup || a?.groupKey)
    const uigB = norm(b?.uigroup || b?.groupKey)
    if (uigA && uigB && uigA === uigB) return true
    if (
      KEAP_TIER_CODES.includes(a?.code) &&
      KEAP_TIER_CODES.includes(b?.code)
    ) {
      return true
    }
    if (MC_TIER_CODES.includes(a?.code) && MC_TIER_CODES.includes(b?.code)) {
      return true
    }
    return false
  }
  const skipDowngradeCodes = ['cc_basicpls-5-thryv-m2m', 'rc-5-thryv-m2m']
  for (const cartCenter of selectedCenters) {
    if (skipDowngradeCodes.includes(cartCenter.code)) continue
    const selectedItem = allProducts.find(p => p.code === cartCenter.code)
    if (!selectedItem) continue
    if (!isSameCenterFamily(item, selectedItem)) continue
    const selectedRank = getProductRank(selectedItem, selectedItem.uigroup)
    const itemRank = getProductRank(item, item.uigroup)
    if (selectedRank >= 0 && itemRank >= 0 && selectedRank > itemRank) {
      return true
    }
  }

  // Special case: SEO product require MC Plus/Pro or a Signature plan
  if (item.code?.includes('seobase-5-thryv-m2m')) {
    const SEO_ELIGIBLE_CENTERS = [
      'mc_plus-5-thryv-m2m',
      'mc_pro-5-thryv-m2m',
      'mc_prov2-5-thryv-m2m',
      'signature-5-thryv-annual',
      'signature_plus-5-thryv-m2m',
      'signature_plus-5-thryv-annual',
    ]
    const hasMCProduct = selectCartCentersNApps.centers?.some(c =>
      SEO_ELIGIBLE_CENTERS.includes(c.code),
    )
    if (!hasMCProduct) {
      return true
    }
  }

  // Check requiresCenter dependencies
  if (item.requiresCenter && item.requiresCenter.length > 0) {
    const hasRequiredCenter = item.requiresCenter.some(requiredCode =>
      selectCartCentersNApps.centers?.some(c => c.code === requiredCode),
    )

    if (!hasRequiredCenter) return true
  }

  // Check requiresAddon dependencies
  if (item.requiresAddon && item.requiresAddon.length > 0) {
    const hasRequiredAddon = item.requiresAddon.some(requiredCode =>
      selectCartCentersNApps.apps?.some(a => a.code === requiredCode),
    )

    if (!hasRequiredAddon) return true
  }

  const effectiveRequires =
    item?.requires?.length > 0 ? item.requires : item?.requiresCenter || []

  // Check requires dependencies (can be group-based)
  if (effectiveRequires.length > 0) {
    const stringReqs = effectiveRequires.filter(r => typeof r === 'string')
    const groupReqs = effectiveRequires.filter(r => r && r.group)

    // String requirements: satisfied if ANY of them are selected (or present as an addon/entitlement)
    const stringSatisfied =
      stringReqs.length === 0 ||
      stringReqs.some(req => {
        if (selectedCodes.includes(req)) return true
        const addonSelectedInApps = selectCartCentersNApps.apps?.some(a =>
          (a.addons || []).some(ad => ad.code === req),
        )
        const addonSelectedInCenters = selectCartCentersNApps.centers?.some(c =>
          (c.addons || []).some(ad => ad.code === req),
        )
        return !!(addonSelectedInApps || addonSelectedInCenters)
      })

    // Group requirements: all group requirements must be satisfied (AND)
    const groupsSatisfied =
      groupReqs.length === 0 ||
      groupReqs.every(req => {
        const groupKey = req.group.toLowerCase()
        const hasGroupItem =
          selectCartCentersNApps.centers?.some(
            c => c.groupKey?.toLowerCase() === groupKey,
          ) ||
          selectCartCentersNApps.apps?.some(
            a => a.groupKey?.toLowerCase() === groupKey,
          )

        return hasGroupItem
      })

    if (!(stringSatisfied && groupsSatisfied)) return true
  }

  // Check disableCenters
  if (item.disableCenters && item.disableCenters.length > 0) {
    const hasDisabledCenter = item.disableCenters.some(disabledCode =>
      selectCartCentersNApps.centers?.some(c => c.code === disabledCode),
    )
    if (hasDisabledCenter) return true
  }

  // Check disableApps
  if (item.disableApps && item.disableApps.length > 0) {
    const hasDisabledApp = item.disableApps.some(disabledCode =>
      selectCartCentersNApps.apps?.some(a => a.code === disabledCode),
    )
    if (hasDisabledApp) return true
  }

  return false
}

// Helper to update disabled items
const updateDisabledItemsHelper = state => {
  const selectCartCentersNApps = state.selectCartCentersNApps || {}
  const defaultCartCentersNApps = state.defaultCartCentersNApps || {}
  const catalogGroups = state.catalogGroups || []
  const centersNAppVList = state.centersNAppVList || {}

  // Safety check
  if (!Array.isArray(catalogGroups)) {
    return
  }

  const disabledItems = {}

  // Get all selected item codes
  const selectedCodes = [
    ...(selectCartCentersNApps.centers || []).map(c => c.code),
    ...(selectCartCentersNApps.apps || []).map(a => a.code),
    ...(selectCartCentersNApps.oneTimeFees || []).map(o => o.code),
  ]

  // Check each catalog item
  catalogGroups.forEach(group => {
    const items = group.items || []
    items.forEach(item => {
      const isDisabled = checkIfItemDisabled(
        item,
        selectedCodes,
        selectCartCentersNApps,
        centersNAppVList,
        defaultCartCentersNApps,
      )
      if (isDisabled) {
        disabledItems[item.code] = true
      }
    })
  })

  state.disabledItems = {...disabledItems}
}

const findUnitAmount = (currencies, selectedCountry) => {
  const currency = currencies?.find(
    currency =>
      currency.currency === CC_CURRENCIES[COUNTRIES[selectedCountry]?.value],
  )
  const unitAmount = currency?.unitAmount
  const needFlag = currency?.needFlag
  return {unitAmount, needFlag}
}

const formatItem = (item, selectedCountry, iconName, groupKey) => {
  const {unitAmount: amount} = findUnitAmount(
    item.currencies,
    selectedCountry,
    null,
  )
  const baseData = {
    code: item.code,
    name: item.name,
    id: item.id,
    prodType: item?.prod_type,
    quantity: 1,
    unitAmount: amount,
    amount: amount,
    img: iconName,
    groupKey: groupKey,
  }
  return baseData
}

const upsertByCode = (items, nextItem) => {
  if (!nextItem || !nextItem.code) return items
  const idx = (items || []).findIndex(i => i.code === nextItem.code)
  if (idx === -1) return [...(items || []), nextItem]
  const merged = {...items[idx], ...nextItem}
  const updated = [...items]
  updated[idx] = merged
  return updated
}

const isKeapCenterCode = code => {
  const normalizedCode = String(code || '').toLowerCase()
  return KEAP_TIER_CODES.includes(code) || normalizedCode.startsWith('keap_')
}

const isStandaloneKeapTextVoiceProduct = product => {
  const normalizedCode = String(product?.code || '')
    .toLowerCase()
    .trim()
  const normalizedName = normalizeProductName(product?.name)

  if (KEAP_TEXT_VOICE_STANDALONE_CODES.has(normalizedCode)) {
    return true
  }

  const looksLikeKeapTextVoiceName =
    normalizedName.includes('keap') &&
    normalizedName.includes('text') &&
    normalizedName.includes('voice') &&
    !normalizedName.includes('included')

  return looksLikeKeapTextVoiceName
}

const isIncludedKeapTextVoiceProduct = product => {
  const normalizedCode = String(product?.code || '')
    .toLowerCase()
    .trim()
  const normalizedName = normalizeProductName(product?.name)
  return (
    KEAP_INCLUDED_TEXT_VOICE_ENTITLEMENT_CODES.has(normalizedCode) ||
    (normalizedName.includes('included') &&
      normalizedName.includes('text') &&
      normalizedName.includes('voice') &&
      normalizedName.includes('keap'))
  )
}

const syncKeapTextVoiceOverride = (
  cartData,
  defaultCartData = {centers: []},
) => {
  if (!cartData) return

  const cartCenters = Array.isArray(cartData.centers) ? cartData.centers : []
  const cartApps = Array.isArray(cartData.apps) ? cartData.apps : []
  const defaultCenters = Array.isArray(defaultCartData?.centers)
    ? defaultCartData.centers
    : []
  const defaultApps = Array.isArray(defaultCartData?.apps)
    ? defaultCartData.apps
    : []

  const hasStandaloneKeapTextVoice = [...cartApps, ...cartCenters].some(
    isStandaloneKeapTextVoiceProduct,
  )

  const stripIncludedFromCenter = center => {
    const currentEntitlements = Array.isArray(center?.entitlements)
      ? center.entitlements
      : []
    const currentAddons = Array.isArray(center?.addons) ? center.addons : []

    const nextEntitlements = currentEntitlements.filter(
      entitlement => !isIncludedKeapTextVoiceProduct(entitlement),
    )
    const nextAddons = currentAddons.filter(
      addon => !isIncludedKeapTextVoiceProduct(addon),
    )

    if (
      nextEntitlements.length === currentEntitlements.length &&
      nextAddons.length === currentAddons.length
    ) {
      return center
    }

    return {
      ...center,
      entitlements: nextEntitlements,
      addons: nextAddons,
    }
  }

  if (hasStandaloneKeapTextVoice) {
    cartData.centers = cartCenters
      .filter(center => !isIncludedKeapTextVoiceProduct(center))
      .map(stripIncludedFromCenter)
    cartData.apps = cartApps.filter(app => !isIncludedKeapTextVoiceProduct(app))
    return
  }

  cartData.centers = cartCenters.filter(Boolean).map(center => ({...center}))
  cartData.apps = cartApps.filter(Boolean).map(app => ({...app}))

  const keapCenterIndex = cartData.centers.findIndex(({code}) =>
    isKeapCenterCode(code),
  )
  const currentKeapCenter =
    keapCenterIndex >= 0 ? cartData.centers[keapCenterIndex] : null

  const defaultKeapCenter =
    defaultCenters.find(({code}) => code === currentKeapCenter?.code) ||
    defaultCenters.find(({code}) => isKeapCenterCode(code))

  // Restore any top-level included Text & Voice entries if they exist in defaults.
  const defaultIncludedCenters = defaultCenters.filter(
    isIncludedKeapTextVoiceProduct,
  )
  const defaultIncludedApps = defaultApps.filter(isIncludedKeapTextVoiceProduct)

  if (defaultIncludedCenters.length > 0) {
    cartData.centers = mergeUniqueProducts(
      cartData.centers,
      defaultIncludedCenters,
    )
  }

  if (defaultIncludedApps.length > 0) {
    cartData.apps = mergeUniqueProducts(cartData.apps, defaultIncludedApps)
  }

  if (keapCenterIndex === -1) return

  const currentEntitlements = Array.isArray(currentKeapCenter?.entitlements)
    ? currentKeapCenter.entitlements
    : []
  const currentAddons = Array.isArray(currentKeapCenter?.addons)
    ? currentKeapCenter.addons
    : []

  const defaultIncludedEntitlements = (
    Array.isArray(defaultKeapCenter?.entitlements)
      ? defaultKeapCenter.entitlements
      : []
  ).filter(isIncludedKeapTextVoiceProduct)

  const defaultIncludedAddons = (
    Array.isArray(defaultKeapCenter?.addons) ? defaultKeapCenter.addons : []
  ).filter(isIncludedKeapTextVoiceProduct)

  const nextEntitlements = mergeUniqueProducts(
    currentEntitlements,
    defaultIncludedEntitlements,
  )
  const nextAddons = mergeUniqueProducts(currentAddons, defaultIncludedAddons)

  if (
    nextEntitlements.length === currentEntitlements.length &&
    nextAddons.length === currentAddons.length
  ) {
    return
  }

  cartData.centers[keapCenterIndex] = {
    ...currentKeapCenter,
    entitlements: nextEntitlements,
    addons: nextAddons,
  }
}

// ============== Redux Slice ==============

const kp11CartSlice = createSlice({
  name: 'kp11',
  initialState,
  reducers: {
    setThryvSubscriptionData(state, action) {
      const {response, processedData} = action.payload

      // Store the raw response
      state.thryvSubscriptionResponse = response

      // If we have processed data (from handleSubscriptionData), use it
      const hasSubscriptions =
        processedData &&
        ((processedData.centers?.length ?? 0) > 0 ||
          (processedData.apps?.length ?? 0) > 0)
      if (hasSubscriptions) {
        state.defaultCartCentersNApps = processedData
        state.selectCartCentersNApps = processedData
      } else {
        // No subscription data, use defaults
        const defaults = getDefaultCentersAndApps()
        state.defaultCartCentersNApps = defaults
        state.selectCartCentersNApps = defaults
      }
    },

    resetToDefaultCart(state) {
      const defaults = getDefaultCentersAndApps()
      state.defaultCartCentersNApps = defaults
      state.selectCartCentersNApps = defaults
      state.thryvSubscriptionResponse = null
      state.keapTextVoiceQuantity = 0
      state.socialAdsSilverQuantity = 0
      state.socialAdsGoldQuantity = 0
      state.socialAdsBronzeQuantity = 0
      state.alphaSEOFromRecurly = {}
      state.selectedKeapTextVoiceOption = ''
      state.selectedKeapKcpOption = ''
      state.selectedKeapAddContactsOption = ''
      state.keapAddContactsQuantity = 0
      state.keapAddUsersQuantity = 0
      state.alphaPowerboostCount = 0
      state.alphaKeyCount = 0
    },
    // Initialize state
    initializeKP11Cart(state, action) {
      const {
        catalogGroups,
        allGroupCustomFieldsMap,
        centersNAppVList,
        defaultAllGroupCustomFieldsMap,
        selectedCountry,
      } = action.payload

      const nextCountry = selectedCountry || 'US'
      const isCountryChanged = state.selectedCountry !== nextCountry

      state.catalogGroups = catalogGroups || []
      state.allGroupCustomFieldsMap = allGroupCustomFieldsMap || {}
      state.centersNAppVList = centersNAppVList || {}
      state.defaultAllGroupCustomFieldsMap =
        defaultAllGroupCustomFieldsMap || {}
      state.selectedCountry = nextCountry
      state.isDataLoaded = state.catalogGroups.length > 0

      // Country switch should clear UI group/cart
      if (isCountryChanged) {
        state.cartItems = {}
        state.catalogCardCollapseStatus = {
          ...initialState.catalogCardCollapseStatus,
        }
        state.parentCardCollapseStatus = {
          ...initialState.parentCardCollapseStatus,
        }
        state.collapseAllTrigger += 1
      }
      if (
        !state?.kp11?.selectCartCentersNApps?.centers?.some(item =>
          item.code.includes('keap'),
        )
      ) {
        state.cartItems = {}
      }
      if (state.isDataLoaded) {
        updateDisabledItemsHelper(state)
      }
    },
    //set Keap Voice Quantity
    setKeapTextVoiceQuantity: (state, action) => {
      state.keapTextVoiceQuantity = action.payload
    },
    //set Keap Selected Voice Option
    setSelectedKeapTextVoiceOption: (state, action) => {
      state.selectedKeapTextVoiceOption = action.payload
    },
    // set Keap Selected KCP certification Option
    setSelectedKeapKcpOption: (state, action) => {
      state.selectedKeapKcpOption = action.payload
    },
    //set Keap Add Contacts Quantuty
    setKeapAddContactsQuantity: (state, action) => {
      state.keapAddContactsQuantity = action.payload
    },
    //set Keap Additional Contacts Option
    setSelectedKeapAddContactsOption: (state, action) => {
      state.selectedKeapAddContactsOption = action.payload
    },
    //set Keap Additional Users Quantity (for downgrade prevention)
    setKeapAddUsersQuantity: (state, action) => {
      state.keapAddUsersQuantity = action.payload
    },
    //set Keap Legacy Graphly Contacts Quantity
    setKeapLegacyGraphlyContactsQuantity: (state, action) => {
      state.keapLegacyGraphlyContactsQuantity = action.payload
    },
    //set Keap Legacy Graphly Contacts Option
    setSelectedKeapLegacyGraphlyContactsOption: (state, action) => {
      state.selectedKeapLegacyGraphlyContactsOption = action.payload
    },
    //set Keap Legacy PlusThis Premium Quantity
    setKeapLegacyPlusThisPremiumQuantity: (state, action) => {
      state.keapLegacyPlusThisPremiumQuantity = action.payload
    },
    //set Keap Legacy PlusThis Premium Option
    setSelectedKeapLegacyPlusThisPremiumOption: (state, action) => {
      state.selectedKeapLegacyPlusThisPremiumOption = action.payload
    },
    //set Alpha SEO Recurly fields
    setAlphaSEOFromRecurly: (state, action) => {
      state.alphaSEOFromRecurly = action.payload
    },
    //set Social Ads Bronze Quantity
    setSocialAdsBronzeQuantity: (state, action) => {
      state.socialAdsBronzeQuantity = action.payload
    },
    //set Social Ads Silver Quantity
    setSocialAdsSilverQuantity: (state, action) => {
      state.socialAdsSilverQuantity = action.payload
    },
    //set Social Ads Gold Quantity
    setSocialAdsGoldQuantity: (state, action) => {
      state.socialAdsGoldQuantity = action.payload
    },
    //set Alpha Power Boost Qty
    setAlphaPowerBoostCount: (state, action) => {
      state.alphaPowerboostCount = action.payload
    },
    //set Alpha Keyword Qty
    setAlphaKeyCount: (state, action) => {
      state.alphaKeyCount = action.payload
    },

    // Handle item selection
    selectItem(state, action) {
      const selectCartCentersNApps = current(state.selectCartCentersNApps)
      const {item} = action.payload
      const selectedCountry = state.selectedCountry
      //getting custom fields
      let homeServicesPaymentType
      const allGroupCustomFieldsMap = current(state.allGroupCustomFieldsMap)
      const homeServicesCustomFields =
        allGroupCustomFieldsMap['homeservices'] || {}
      if (homeServicesCustomFields) {
        homeServicesPaymentType = homeServicesCustomFields?.paymentOption
      }
      const shouldAllowMultipleInSameGroup = (code1, code2) => {
        const ALLOW_MULTIPLE_CODES = [
          'tl-mc-5-thryv-m2m',
          'seobase-5-thryv-m2m',
        ]
        // If both codes are in the allow list, they can coexist
        return (
          ALLOW_MULTIPLE_CODES.includes(code1) &&
          ALLOW_MULTIPLE_CODES.includes(code2) &&
          code1 !== code2
        )
      }
      const centersNAppVList = current(state.centersNAppVList)
      const defaultCartCentersNApps = current(state.defaultCartCentersNApps)
      const hasPaidOnboardingFee =
        state.thryvSubscriptionResponse?.accountInfo?.hasPaidOnboardingFee
      const hasPaidDesignFee =
        state.thryvSubscriptionResponse?.accountInfo?.hasPaidDesignFee

      const data = {
        centers: selectCartCentersNApps.centers,
        apps: selectCartCentersNApps.apps,
        oneTimeFees: selectCartCentersNApps.oneTimeFees,
      }
      const centerType =
        item.section === 'centers' && item.groupKey?.split(' ')[0]
      let prevCenter
      if (item?.groupKey?.includes(centerType)) {
        const sameGroups = state.catalogGroups.find(
          group => group.key.toLowerCase() === centerType.toLowerCase(),
        )
        const sameGroupItems = sameGroups?.items || []

        prevCenter = data.centers.find(center =>
          sameGroupItems?.some(group => group.code === center.code),
        )
        // Check if these products can coexist
        const canCoexist =
          prevCenter &&
          shouldAllowMultipleInSameGroup(item.code, prevCenter.code)
        // Only replace if they cannot coexist
        if (prevCenter && !canCoexist) {
          data.centers = data.centers.filter(
            plan => !plan.code.includes(prevCenter?.code),
          )
        }
      }

      // Keap group: mutual exclusion — when adding a new core Keap product,
      // remove any previously selected core Keap product so the cart only keeps the latest one.
      if (item?.groupKey?.toLowerCase() === 'keap' || isKeapCoreProduct(item)) {
        const replacedKeapItem = [...data.centers, ...data.apps].find(
          existingItem =>
            isKeapCoreProduct(existingItem) && existingItem.code !== item.code,
        )
        const selectedKeapCodes = new Set(
          [...data.centers, ...data.apps]
            .filter(existingItem => isKeapCoreProduct(existingItem))
            .map(existingItem => existingItem.code),
        )
        selectedKeapCodes.delete(item.code)
        if (replacedKeapItem) {
          item.replacedKeapItem = replacedKeapItem
        }

        if (selectedKeapCodes.size > 0) {
          data.centers = data.centers.filter(
            center => !selectedKeapCodes.has(center.code),
          )
          data.apps = data.apps.filter(app => !selectedKeapCodes.has(app.code))
          data.oneTimeFees = data.oneTimeFees.filter(
            fee => !selectedKeapCodes.has(fee.code),
          )
          Object.keys(state.cartItems).forEach(code => {
            if (selectedKeapCodes.has(code)) {
              delete state.cartItems[code]
            }
          })
        }
      }
      // TL/GP logic — applies to all TL SKU variants
      const TL_ALL_CODES = [
        'tl-mc-5-thryv-m2m',
        'tl-tax-5-thryv-m2m',
        'tl-5-thryv-m2m',
      ]
      const GP_STARTER_CODE = 'addon-5-mcstartgr'
      const GP_EXPANDED_CODE = 'addon-5-mcexpangr'

      if (TL_ALL_CODES.includes(item.code)) {
        // If TL is being added, remove any GP from cart
        data.apps = data.apps.filter(
          app => ![GP_STARTER_CODE, GP_EXPANDED_CODE].includes(app.code),
        )
        // Sync cartItems so the catalog UI doesn't still show GP as "added"
        delete state.cartItems[GP_STARTER_CODE]
        delete state.cartItems[GP_EXPANDED_CODE]
      }

      // Convrrt Landing Page Pro/Lite are mutually exclusive.
      const allCatalogProducts = [
        ...(centersNAppVList.centers || []),
        ...(centersNAppVList.apps || []),
        ...(centersNAppVList.oneTimeFees || []),
      ]
      const convrrtProCodes = allCatalogProducts
        .filter(product => isConvrrtLPPro(product?.name))
        .map(product => product.code)
      const convrrtLiteCodes = allCatalogProducts
        .filter(product => isConvrrtLPLite(product?.name))
        .map(product => product.code)

      if (isConvrrtLPPro(item?.name)) {
        data.apps = data.apps.filter(
          app => !convrrtLiteCodes.includes(app.code),
        )
        data.centers = data.centers.filter(
          center => !convrrtLiteCodes.includes(center.code),
        )
        data.oneTimeFees = data.oneTimeFees.filter(
          fee => !convrrtLiteCodes.includes(fee.code),
        )
      }

      if (isConvrrtLPLite(item?.name)) {
        data.apps = data.apps.filter(app => !convrrtProCodes.includes(app.code))
        data.centers = data.centers.filter(
          center => !convrrtProCodes.includes(center.code),
        )
        data.oneTimeFees = data.oneTimeFees.filter(
          fee => !convrrtProCodes.includes(fee.code),
        )
      }

      // Convrrt Lite/Pro (standalone, non-landing-page) are mutually exclusive.
      const CONVRRT_LITE_CODE = 'addon-5-keap-convrrtlp-lite'
      const CONVRRT_PRO_CODE = 'addon-5-keap-convrrtlp-pro'
      const CONVRRT_LITE_ANNUAL_CODE = 'addon-5-keap-convrrtlp-lite-annual'
      const CONVRRT_PRO_ANNUAL_CODE = 'addon-5-keap-convrrtlp-pro-annual'
      if (item?.code === CONVRRT_PRO_CODE) {
        data.apps = data.apps.filter(app => app.code !== CONVRRT_LITE_CODE)
      }
      if (item?.code === CONVRRT_LITE_CODE) {
        data.apps = data.apps.filter(app => app.code !== CONVRRT_PRO_CODE)
      }
      if (item?.code === CONVRRT_PRO_ANNUAL_CODE) {
        data.apps = data.apps.filter(
          app => app.code !== CONVRRT_LITE_ANNUAL_CODE,
        )
      }
      if (item?.code === CONVRRT_LITE_ANNUAL_CODE) {
        data.apps = data.apps.filter(
          app => app.code !== CONVRRT_PRO_ANNUAL_CODE,
        )
      }

      const isMCPlusProInDefault = defaultCartCentersNApps.centers.some(
        center =>
          center.code.includes('mc_plus-5-thryv-m2m') ||
          center.code.includes('mc_pro-5-thryv-m2m'),
      )
      const isHomeServicesDefault = defaultCartCentersNApps?.apps?.some(app =>
        [
          'maxhvac-5-thryv-m2m',
          'maxroof-5-thryv-m2m',
          'maxelect-5-thryv-m2m',
          'maxplumb-5-thryv-m2m',
        ].includes(app.code),
      )
      const isHomeServicesInCart = data?.apps?.some(app =>
        [
          'maxhvac-5-thryv-m2m',
          'maxroof-5-thryv-m2m',
          'maxelect-5-thryv-m2m',
          'maxplumb-5-thryv-m2m',
        ].includes(app.code),
      )
      const isMCPlusInCart = data?.centers?.some(
        ({code}) =>
          !item?.code.toLowerCase().includes('mc_pro-5-thryv-m2m') &&
          (['mc_plus-5-thryv-m2m'].includes(code) ||
            item?.code.toLowerCase().includes('mc_plus-5-thryv-m2m')),
      )
      const isMCProInCart = data?.centers?.some(
        ({code}) =>
          ['mc_pro-5-thryv-m2m'].includes(code) ||
          item?.code.toLowerCase().includes('mc_pro-5-thryv-m2m'),
      )
      const isBCAlreadyPresent = defaultCartCentersNApps?.centers?.filter(
        center => center.name.toLowerCase().includes('business'),
      )
      const isMCAlreadyPresent = defaultCartCentersNApps?.centers?.filter(
        center => center.name.toLowerCase().includes('marketing'),
      )
      const isRCAlreadyPresent = defaultCartCentersNApps?.centers?.filter(
        center => center.name.toLowerCase().includes('reporting'),
      )
      //const [socialAdsQuantity, setSocialAdsQuantity] = useState(1)
      const isMCPlusProPlan =
        item?.code.toLowerCase().includes('mc_plus-5-thryv-m2m') ||
        item?.code.toLowerCase().includes('mc_pro-5-thryv-m2m')
      const isHomeServices = [
        'maxhvac-5-thryv-m2m',
        'maxroof-5-thryv-m2m',
        'maxelect-5-thryv-m2m',
        'maxplumb-5-thryv-m2m',
      ].includes(item?.code)
      if (item.section === 'centers') {
        //const centerType = item.code.split('_')[0]
        const {autoAddCenters = [], ...centerToAdd} = item
        if (item.code === 'mc_pro-5-thryv-m2m') {
          const isWebsiteAppInCart = data.apps.some(
            ({code}) => code === 'addon-5-website_difm',
          )
          if (isWebsiteAppInCart) {
            // const websiteCustomFields = data.apps.find(
            //   ({code}) => code === 'addon-5-website_difm',
            // )?.customFields
            // const sensisSiteDomainInCart = websiteCustomFields?.sensisDomain
            //   ? websiteCustomFields.sensisDomain
            //   : null
            // const centerWithWebsiteEnt = item?.addons.find(
            //   ({code}) => code === 'ent-5-website',
            // )
            // const newEntitlements = centerWithWebsiteEnt?.entitlements?.map(
            //   ent => {
            //     if (ent.code === 'ent-5-website') {
            //       return {
            //         ...ent,
            //         ...(sensisSiteDomainInCart && {
            //           sensisSiteDomain: sensisSiteDomainInCart,
            //         }),
            //       }
            //     }
            //     return ent
            //   },
            // )
            // console.log('newEntitlements' + JSON.stringify(newEntitlements))
            // const mcProCenterToAdd = {
            //   ...centerToAdd,
            //   entitlements: newEntitlements,
            // }
            // console.log('mcProCenterToAdd' + JSON.stringify(mcProCenterToAdd))
            data.apps = data?.apps?.filter(
              ({code}) => code !== 'addon-5-website_difm',
            )
            //data.centers = upsertByCode(data.centers, mcProCenterToAdd)
          }
          data.centers = upsertByCode(data.centers, centerToAdd)
        } else {
          data.centers = upsertByCode(data.centers, centerToAdd)
        }

        if (autoAddCenters.length) {
          autoAddCenters.forEach(autoAddCenter => {
            const isCenterInOrder = data.centers.some(
              ({code}) => code === autoAddCenter.code,
            )
            if (isCenterInOrder) return
            if (autoAddCenter.code === 'rc-5-thryv-m2m') {
              // Don't auto add RC , incase of BC/MC upgrade or if RC Basic/Plus is already present
              let isBcMcUpgrade = false
              if (
                (centerToAdd.name.toLowerCase().includes('business') &&
                  isBCAlreadyPresent.length > 0) ||
                (centerToAdd.name.toLowerCase().includes('marketing') &&
                  isMCAlreadyPresent.length > 0)
              ) {
                isBcMcUpgrade = true
              }
              if (isRCAlreadyPresent.length > 0 || isBcMcUpgrade) {
                return
              }
            }
            data.centers = upsertByCode(
              data.centers,
              formatItem(autoAddCenter, selectedCountry),
            )
          })
        }
        const existingCodesCenter = [
          ...defaultCartCentersNApps.centers.map(c => c.code),
          ...(defaultCartCentersNApps.apps || []).map(a => a.code),
        ]
        const cartCodesCenter = [
          ...data.centers.map(c => c.code),
          ...data.apps.map(a => a.code),
          item.code,
        ].filter((v, i, arr) => arr.indexOf(v) === i)
        const applicableFeeCodesCenter = resolveApplicableFees(
          existingCodesCenter,
          cartCodesCenter,
          centersNAppVList?.oneTimeFees || [],
        )
        const oneTimeFeesCatalogCenter = centersNAppVList?.oneTimeFees || []
        const managedFeeCodesCenter = new Set(
          oneTimeFeesCatalogCenter.map(f => f.code),
        )
        const retainedCenter = data.oneTimeFees.filter(
          fee =>
            !managedFeeCodesCenter.has(fee.code) ||
            applicableFeeCodesCenter.includes(fee.code),
        )
        const feesToAddCenter = oneTimeFeesCatalogCenter
          .filter(fee => applicableFeeCodesCenter.includes(fee.code))
          .filter(fee => !retainedCenter.some(o => o.code === fee.code))
          .map(fee => formatItem(fee, selectedCountry, null, 'oneTimeFees'))
        const entitlementOneTimes = (
          item.entitlements?.flatMap(ent => ent.applicableOneTimes) || []
        ).filter(
          ot =>
            ot &&
            !retainedCenter.some(o => o.code === ot.code) &&
            !feesToAddCenter.some(o => o.code === ot.code),
        )
        data.oneTimeFees = [
          ...retainedCenter,
          ...feesToAddCenter,
          ...entitlementOneTimes,
        ]
      } else if (item.section === 'apps') {
        const existingCodesApp = [
          ...defaultCartCentersNApps.centers.map(c => c.code),
          ...(defaultCartCentersNApps.apps || []).map(a => a.code),
        ]
        const cartCodesApp = [
          ...data.centers.map(c => c.code),
          ...data.apps.map(a => a.code),
          item.code,
        ].filter((v, i, arr) => arr.indexOf(v) === i)
        const applicableFeeCodesApp = resolveApplicableFees(
          existingCodesApp,
          cartCodesApp,
          centersNAppVList?.oneTimeFees || [],
        )
        const oneTimeFeesCatalogApp = centersNAppVList?.oneTimeFees || []
        // Adding an app should never remove existing OTFs — only add newly
        // applicable ones. The retainedApp removal pattern was incorrectly
        // dropping OTFs added by center selection or explicit user choice
        // whenever their appliesTo rule wasn't re-matched in this context.
        const feesToAddApp = oneTimeFeesCatalogApp
          .filter(fee => applicableFeeCodesApp.includes(fee.code))
          .filter(fee => !data.oneTimeFees.some(o => o.code === fee.code))
          .map(fee => formatItem(fee, selectedCountry, null, 'oneTimeFees'))
        const entitlementOneTimesApp = (
          item.entitlements?.flatMap(ent => ent.applicableOneTimes) || []
        ).filter(
          ot =>
            ot &&
            !data.oneTimeFees.some(o => o.code === ot.code) &&
            !feesToAddApp.some(o => o.code === ot.code),
        )
        data.oneTimeFees = [
          ...data.oneTimeFees,
          ...feesToAddApp,
          ...entitlementOneTimesApp,
        ]
        if (item.code === 'addon-5-mcexpangr') {
          data.apps = data.apps.filter(
            plan => plan.code !== 'addon-5-mcstartgr',
          )
        }
        if (
          item.code.includes('addon-socialads') &&
          data.apps.find(app => app.code === item.code)
        ) {
          data.apps = data.apps.map(app => {
            if (app.code === item.code) {
              //item.quantity = 1 //socialAdsQuantity
              return {
                ...app,
                unitAmount:
                  item.unitAmount > 0 ? item.unitAmount : app.unitAmount,
                amount: item.amount > 0 ? item.amount : app.amount,
                quantity: item.quantity > 0 ? item.quantity : app.quantity,
                isUpgraded: app.isAlreadyAdded ? true : false,
              }
            }
            return app
          })
        } else if (
          item.code === 'addon-5-keap-addcontacts-m2m' &&
          data.apps.find(app => app.code === item.code)
        ) {
          data.apps = data.apps.map(app => {
            if (app.code === 'addon-5-keap-addcontacts-m2m') {
              return {
                ...app,
                isUpgraded: app.isAlreadyAdded ? true : false,
                unitAmount:
                  item.unitAmount > 0 ? item.unitAmount : app.unitAmount,
                amount: item.unitAmount > 0 ? item.unitAmount : app.unitAmount,
              }
            }
            return app
          })
        } else if (
          item.code === 'addon-5-keap-textvoice-m2m' &&
          data.apps.find(app => app.code === item.code)
        ) {
          data.apps = data.apps.map(app => {
            if (app.code === 'addon-5-keap-textvoice-m2m') {
              return {
                ...app,
                isUpgraded: app.isAlreadyAdded ? true : false,
                unitAmount:
                  item.unitAmount > 0 ? item.unitAmount : app.unitAmount,
                amount: item.unitAmount > 0 ? item.unitAmount : app.unitAmount,
              }
            }
            return app
          })
        } else if (
          item.code === 'addon-5-keap-addusers-m2m' &&
          data.apps.find(app => app.code === item.code)
        ) {
          data.apps = data.apps.map(app => {
            if (app.code === 'addon-5-keap-addusers-m2m') {
              return {
                ...app,
                isUpgraded: app.isAlreadyAdded ? true : false,
                quantity: item.quantity,
                unitAmount: item.unitAmount,
                amount: item.amount,
              }
            }
            return app
          })
        } else if (item.code === 'addon-5-website_difm') {
          if (isMCProInCart) {
            let mcProCenter = data.centers.find(
              ({code}) => code === 'mc_pro-5-thryv-m2m',
            )
            const hasMCWebsiteEntitlement = mcProCenter.addons.find(
              ({code}) => code === 'ent-5-website',
            )
            if (!hasMCWebsiteEntitlement) {
              const websiteEntitlement = {
                code: 'ent-5-website',
                name: 'Professionally Designed Website',
                quantity: 1,
                amount: 0,
                unitAmount: 0,
              }
              mcProCenter = {
                ...mcProCenter,
                addons: [...(mcProCenter.addons || []), websiteEntitlement],
              }
            }
            data.centers = data.centers.map(center =>
              center.code === 'mc_pro-5-thryv-m2m' ? mcProCenter : center,
            )
            data.apps = data?.apps?.filter(
              ({code}) => code !== 'addon-5-website_difm',
            )
          } else {
            data.apps = upsertByCode(data.apps, item)
          }
        } else {
          data.apps = upsertByCode(data.apps, item)
        }
      } else {
        data.oneTimeFees = upsertByCode(data.oneTimeFees, item)
      }
      if (
        (KEAP_TIER_CODES.includes(item.code) ||
          KEAP_FAMILY_CODES.includes(item.code)) &&
        item.code.includes('keap') &&
        !item.code.includes('addon-') &&
        !item.code.includes('-ot')
      ) {
        const alreadyHasKeapAddonParent = data.apps.some(app =>
          KEAP_ADDONS_PARENT_CODES.includes(app.code),
        )
        if (!alreadyHasKeapAddonParent) {
          let keapAddonParentCode = 'addon-5-keap-addons'
          if (item.code.includes('annual')) {
            keapAddonParentCode = 'addon-5-keap-addons-annual'
          }
          data.apps = upsertByCode(data.apps, {
            id: 'keapaddons',
            code: keapAddonParentCode,
            name: 'Keap Add-Ons',
            quantity: 1,
            groupTitle: 'Keap Add-Ons',
            unitAmount: 0,
            amount: 0,
            addons: [],
          })
        }
      }

      // // Leads selection clears thryvAddons except already-added items/addons
      // if (item.groupKey === 'thryvLeads') {
      //   updated = updateGroupItems(
      //     updated,
      //     g => g.key === 'thryvAddons',
      //     g => ({
      //       ...g,
      //       items: keepOnlyAlreadyAddedAddons(g.items),
      //     }),
      //   )
      // }

      // // Marketing Center logic
      // if (
      //   item.groupKey === 'marketingCenter' ||
      //   item.groupKey === 'marketingcenter'
      // ) {
      //   updated = addApplicableMCOneTimes(
      //     updated,
      //     item,
      //     selectedCountry,
      //     centersNAppVList,
      //     defaultCartCentersNApps,
      //   )
      // }

      // // Auto-add items for specific groups
      // updated = addAutoItemsForGroup(
      //   updated,
      //   item.groupKey,
      //   selectedCountry,
      //   centersNAppVList,
      // )
      // console.log('updated ' + JSON.stringify(updated))

      const hasWebsiteEntitlement = [...data.centers, ...data.apps].some(
        cartItem =>
          !MC_2_0_PLANS.includes(cartItem.code) &&
          (cartItem.code === 'addon-5-website_difm' ||
            (cartItem.addons || []).some(
              addon =>
                addon.code === 'addon-5-website_difm' ||
                addon.code?.toLowerCase()?.includes('ent-5-website'),
            )),
      )

      if (hasWebsiteEntitlement) {
        const hasDesignFee = data.oneTimeFees.some(
          fee => fee.code === WEBSITE_DESIGN_FEE_ENTITLEMENT.code,
        )
        if (!hasDesignFee) {
          const websiteDesignFee = formatItem(
            WEBSITE_DESIGN_FEE_ENTITLEMENT,
            selectedCountry,
            null,
            'oneTimeFees',
          )
          data.oneTimeFees = [...data.oneTimeFees, websiteDesignFee]
        }
      } else {
        data.oneTimeFees = data.oneTimeFees.filter(
          fee => fee.code !== WEBSITE_DESIGN_FEE_ENTITLEMENT.code,
        )
      }
      const keapMaxIcon = fetchPlanIcon('keap')
      const hvacMcServicesM2MAddOn = centersNAppVList.apps
        .map(app =>
          formatItem(app, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(app => app.code === 'vert_mc_clientsvcs-5-thryv-m2m')[0]
      const hvacMcServicesDesignM2MAddOn = centersNAppVList.apps
        .map(app =>
          formatItem(app, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(app => app.code === 'vert_mc_web_clientsvcs-5-thryv-m2m')[0]
      const hvacServicesM2MAddOn = centersNAppVList.apps
        .map(app =>
          formatItem(app, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(app => app.code === 'vert_clientsvcs-5-thryv-m2m')[0]
      const hvacMcServicesOtAddOn = centersNAppVList.oneTimeFees
        .map(oneTimeFee =>
          formatItem(oneTimeFee, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(
          oneTimeFee => oneTimeFee.code === 'vert_mc_clientsvcs-5-thryv-ot',
        )[0]
      const hvacMcServicesDesignOtAddOn = centersNAppVList.oneTimeFees
        .map(oneTimeFee =>
          formatItem(oneTimeFee, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(
          oneTimeFee => oneTimeFee.code === 'vert_mc_web_clientsvcs-5-thryv-ot',
        )[0]
      const hvacServicesOtAddOn = centersNAppVList.oneTimeFees
        .map(oneTimeFee =>
          formatItem(oneTimeFee, selectedCountry, keapMaxIcon, 'Keap Add-Ons'),
        )
        .filter(
          oneTimeFee => oneTimeFee.code === 'vert_clientsvcs-5-thryv-ot',
        )[0]
      let waiveHVACFees = false
      if (!isHomeServicesDefault && (isHomeServices || isHomeServicesInCart)) {
        if (isHomeServices || isMCPlusProPlan) {
          if (!isMCPlusProInDefault) {
            if (isMCPlusInCart || isMCProInCart) {
              if (isMCPlusInCart) {
                //this is to make sure we do not add the vertical as duplicate
                data.apps = data.apps.filter(
                  app =>
                    app.code !== 'vert_clientsvcs-5-thryv-m2m' &&
                    app.code !== 'vert_mc_clientsvcs-5-thryv-m2m' &&
                    app.code !== 'vert_mc_web_clientsvcs-5-thryv-m2m',
                )
                data.oneTimeFees = data.oneTimeFees.filter(
                  oneTime =>
                    oneTime.code !== 'vert_clientsvcs-5-thryv-ot' &&
                    oneTime.code !== 'vert_mc_clientsvcs-5-thryv-ot' &&
                    oneTime.code !== 'vert_mc_web_clientsvcs-5-thryv-ot',
                )
                switch (homeServicesPaymentType) {
                  case 'oneTime':
                    data.oneTimeFees.push(hvacMcServicesOtAddOn)
                    break
                  case '6Monthly':
                    data.apps.push(hvacMcServicesM2MAddOn)
                    break
                  default:
                    break
                }
                waiveHVACFees = true
              } else if (isMCProInCart) {
                const mcProWebsite = selectCartCentersNApps.centers.find(
                  center => center.code === 'mc_pro-5-thryv-m2m',
                )
                const selectedOptionsWebsite = item?.addons?.find(
                  ({code}) => code === 'ent-5-website',
                )
                const websiteEnt = mcProWebsite?.addons?.find(
                  ({code}) => code === 'ent-5-website',
                )
                waiveHVACFees = true
                if (!websiteEnt && !selectedOptionsWebsite) {
                  data.apps = data.apps.filter(
                    app =>
                      app.code !== 'vert_clientsvcs-5-thryv-m2m' &&
                      app.code !== 'vert_mc_clientsvcs-5-thryv-m2m' &&
                      app.code !== 'vert_mc_web_clientsvcs-5-thryv-m2m',
                  )
                  data.oneTimeFees = data.oneTimeFees.filter(
                    oneTime =>
                      oneTime.code !== 'vert_clientsvcs-5-thryv-ot' &&
                      oneTime.code !== 'vert_mc_clientsvcs-5-thryv-ot' &&
                      oneTime.code !== 'vert_mc_web_clientsvcs-5-thryv-ot',
                  )
                  switch (homeServicesPaymentType) {
                    case 'oneTime':
                      data.oneTimeFees.push(hvacMcServicesOtAddOn)
                      break
                    case '6Monthly':
                      data.apps.push(hvacMcServicesM2MAddOn)
                      break
                    default:
                      break
                  }
                  waiveHVACFees = true
                } else {
                  data.apps = data.apps.filter(
                    app =>
                      app.code !== 'vert_clientsvcs-5-thryv-m2m' &&
                      app.code !== 'vert_mc_clientsvcs-5-thryv-m2m' &&
                      app.code !== 'vert_mc_web_clientsvcs-5-thryv-m2m',
                  )
                  data.oneTimeFees = data.oneTimeFees.filter(
                    oneTime =>
                      oneTime.code !== 'vert_clientsvcs-5-thryv-ot' &&
                      oneTime.code !== 'vert_mc_clientsvcs-5-thryv-ot' &&
                      oneTime.code !== 'vert_mc_web_clientsvcs-5-thryv-ot',
                  )
                  switch (homeServicesPaymentType) {
                    case 'oneTime':
                      data.oneTimeFees.push(hvacMcServicesDesignOtAddOn)
                      break
                    case '6Monthly':
                      data.apps.push(hvacMcServicesDesignM2MAddOn)
                      break
                    default:
                      break
                  }
                  waiveHVACFees = true
                }
              }
            } else {
              data.apps = data.apps.filter(
                app =>
                  app.code !== 'vert_clientsvcs-5-thryv-m2m' &&
                  app.code !== 'vert_mc_clientsvcs-5-thryv-m2m' &&
                  app.code !== 'vert_mc_web_clientsvcs-5-thryv-m2m',
              )
              data.oneTimeFees = data.oneTimeFees.filter(
                oneTime =>
                  oneTime.code !== 'vert_clientsvcs-5-thryv-ot' &&
                  oneTime.code !== 'vert_mc_clientsvcs-5-thryv-ot' &&
                  oneTime.code !== 'vert_mc_web_clientsvcs-5-thryv-ot',
              )
              switch (homeServicesPaymentType) {
                case 'oneTime':
                  data.oneTimeFees.push(hvacServicesOtAddOn)
                  break
                case '6Monthly':
                  data.apps.push(hvacServicesM2MAddOn)
                  break
                default:
                  break
              }
              waiveHVACFees = true
            }
          } else {
            data.apps = data.apps.filter(
              app =>
                app.code !== 'vert_clientsvcs-5-thryv-m2m' &&
                app.code !== 'vert_mc_clientsvcs-5-thryv-m2m' &&
                app.code !== 'vert_mc_web_clientsvcs-5-thryv-m2m',
            )
            data.oneTimeFees = data.oneTimeFees.filter(
              oneTime =>
                oneTime.code !== 'vert_clientsvcs-5-thryv-ot' &&
                oneTime.code !== 'vert_mc_clientsvcs-5-thryv-ot' &&
                oneTime.code !== 'vert_mc_web_clientsvcs-5-thryv-ot',
            )
            switch (homeServicesPaymentType) {
              case 'oneTime':
                data.oneTimeFees.push(hvacServicesOtAddOn)
                break
              case '6Monthly':
                data.apps.push(hvacServicesM2MAddOn)
                break
              default:
                break
            }
            waiveHVACFees = true
          }
        }
      }
      if (waiveHVACFees) {
        data.oneTimeFees = data.oneTimeFees.filter(
          ({code}) => code !== 'thryvprfee' && code !== 'thryvdesg2',
        )
      }
      if (item.code === 'seobase-5-thryv-m2m') {
        // Check for SEO center and remove addons array
        const seoCenter = data.centers?.find(
          center => center.code === 'seobase-5-thryv-m2m',
        )
        if (seoCenter) {
          delete seoCenter.addons
        }
        data.apps = data.apps.filter(
          app =>
            app.code !== 'addon-5-seokey-m2m' &&
            app.code !== 'addon-5-seopb-m2m',
        )
        item?.addons?.forEach(addon => {
          data.apps.push(addon)
        })
      }
      const isKeapInCart = data.centers.some(c =>
        KEAP_TIER_CODES.includes(c.code),
      )
      data.oneTimeFees = data.oneTimeFees.filter(item => {
        if (
          item.code === 'thryvprfee' &&
          hasPaidOnboardingFee &&
          !isKeapInCart
        ) {
          return false
        }
        if (item.code === 'thryvdesg2' && hasPaidDesignFee) {
          return false
        }
        return true
      })

      syncKeapTextVoiceOverride(data, defaultCartCentersNApps)

      state.selectCartCentersNApps = data
      state.cartItems[item.code] = item
      state.collapseAllTrigger += 1
      if (
        !state.parentCardCollapseStatus[item.groupKey] &&
        (Object.keys(state.cartItems) > 0 ||
          state.cartItems[item.code]?.code !== item.code)
      ) {
        Object.keys(state.catalogCardCollapseStatus).forEach(k => {
          state.catalogCardCollapseStatus[k] = false
        })
      }
      updateDisabledItemsHelper(state)
    },

    // Handle item removal
    removeItem(state, action) {
      const {groupKey, itemCode} = action.payload
      const centersNAppVList = current(state.centersNAppVList)
      const defaultCartCentersNApps = current(state.defaultCartCentersNApps)
      const selectCartCentersNApps = current(state.selectCartCentersNApps)
      let dependentProds = []
      let productsToRemove = []
      let modifiedCenters
      let modifiedApps
      let modifiedOneTimes
      //////////////////////////////////////////////////
      // simple removal logic
      //////////////////////////////////////////////////
      const refProdData = [
        ...centersNAppVList.centers,
        ...centersNAppVList.apps,
        ...centersNAppVList.oneTimeFees,
      ]

      let allProducts = [
        ...selectCartCentersNApps.centers
          .filter(item => item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'Center',
            }
          }),
        ...(selectCartCentersNApps?.apps || [])
          .filter(item => item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'App',
            }
          }),
        ...selectCartCentersNApps.oneTimeFees
          .filter(item => item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'oneTimeFee',
            }
          }),
      ]
      // Added newly to the order
      let newlyAddedProducts = [
        ...selectCartCentersNApps.centers
          .filter(item => !item.isAlreadyAdded && item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'Center',
              configuration: centersNAppVList.centers.find(
                center => center.code === item.code,
              ),
            }
          }),
        ...(selectCartCentersNApps?.apps || [])
          .filter(item => !item.isAlreadyAdded && item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'App',
              configuration: centersNAppVList.apps.find(
                app => app.code === item.code,
              ),
            }
          }),
        ...selectCartCentersNApps.oneTimeFees
          .filter(item => !item.isAlreadyAdded && item.code !== itemCode)
          .map(item => {
            return {
              code: item.code,
              prodType: 'oneTimeFee',
              configuration: centersNAppVList.oneTimeFees.find(
                oneTimeProd => oneTimeProd.code === item.code,
              ),
            }
          }),
      ]
      // Upgrade/Downgrade logic for Marketing center and Growth packages //Quantity reset logic for variable priced items
      if (
        itemCode === 'mc_pro-5-thryv-m2m' &&
        defaultCartCentersNApps.centers.find(
          item => item.code === 'mc_plus-5-thryv-m2m',
        )
      ) {
        let mcPlus = defaultCartCentersNApps.centers.find(
          item => item.code === 'mc_plus-5-thryv-m2m',
        )
        modifiedCenters = selectCartCentersNApps.centers.map(item =>
          item.code === itemCode ? mcPlus : item,
        )
      } else if (
        itemCode === 'addon-5-mcexpangr' &&
        defaultCartCentersNApps.apps.find(
          item => item.code === 'addon-5-mcstartgr',
        )
      ) {
        let starterGrowth = defaultCartCentersNApps.apps.find(
          item => item.code === 'addon-5-mcstartgr',
        )
        modifiedApps = selectCartCentersNApps.apps.map(item =>
          item.code === itemCode ? starterGrowth : item,
        )
      } else if (
        itemCode === 'addon-socialadsbronze-5-m2m' ||
        itemCode === 'addon-socialadssilver-5-m2m' ||
        itemCode === 'addon-socialadsgold-5-m2m' ||
        itemCode === 'addon-5-seopb-m2m' ||
        itemCode === 'addon-5-seokey-m2m' ||
        itemCode === 'addon-5-keap-addusers-m2m' ||
        itemCode === 'addon-5-keap-addcontacts-m2m' ||
        itemCode === 'addon-5-keap-textvoice-m2m'
      ) {
        let currentApp = defaultCartCentersNApps.apps.find(
          item => item.code === itemCode,
        )
        if (currentApp) {
          modifiedApps = selectCartCentersNApps.apps.map(item =>
            item.code === itemCode ? currentApp : item,
          )
        } else {
          productsToRemove.push({code: itemCode})
        }
      } else if (itemCode === 'seobase-5-thryv-m2m') {
        let currentDefault =
          defaultCartCentersNApps.centers.find(
            item => item.code === itemCode,
          ) ||
          defaultCartCentersNApps.apps?.find(item => item.code === itemCode)
        if (currentDefault) {
          const isInCenters = selectCartCentersNApps.centers.some(
            item => item.code === itemCode,
          )
          const isInApps = selectCartCentersNApps.apps.some(
            item => item.code === itemCode,
          )
          if (isInCenters) {
            modifiedCenters = selectCartCentersNApps.centers.map(item =>
              item.code === itemCode ? currentDefault : item,
            )
          } else if (isInApps) {
            modifiedApps = selectCartCentersNApps.apps.map(item =>
              item.code === itemCode ? currentDefault : item,
            )
          }
        } else {
          productsToRemove.push({code: itemCode})
        }
      } else if (KEAP_TIER_CODES.includes(itemCode)) {
        // Removing a newly added Keap tier (e.g. Keap Max Partner): restore the existing
        // Keap plan from account (e.g. Keap Pro) so Order Summary shows "Existing".
        const removedKeapItem = state.cartItems[itemCode]
        const restoredKeapItem =
          removedKeapItem?.replacedKeapItem ||
          defaultCartCentersNApps.centers.find(c =>
            KEAP_TIER_CODES.includes(c.code),
          )
        if (restoredKeapItem) {
          modifiedCenters = selectCartCentersNApps.centers.map(item =>
            item.code === itemCode ? restoredKeapItem : item,
          )
          state.cartItems[restoredKeapItem.code] = restoredKeapItem
        } else {
          productsToRemove.push({code: itemCode})
        }
      } else if (
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          itemCode,
        )
      ) {
        // Removing TL: restore any GP that existed in the default cart
        productsToRemove.push({code: itemCode})
        const defaultGP = defaultCartCentersNApps.apps.find(
          app =>
            app.code === 'addon-5-mcstartgr' ||
            app.code === 'addon-5-mcexpangr',
        )
        if (defaultGP) {
          modifiedApps = [...selectCartCentersNApps.apps, defaultGP]
        }
      } else {
        productsToRemove.push({code: itemCode})
      }
      if (
        itemCode === 'mc_pro-5-thryv-m2m' ||
        itemCode === 'mc_plus-5-thryv-m2m'
      ) {
        if (
          newlyAddedProducts.find(item => item.code === 'seobase-5-thryv-m2m')
        ) {
          productsToRemove.push({
            code: 'seobase-5-thryv-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'addon-5-seokey-m2m')
        ) {
          productsToRemove.push({
            code: 'addon-5-seokey-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'addon-5-seopb-m2m')
        ) {
          productsToRemove.push({
            code: 'addon-5-seopb-m2m',
            prodType: 'App',
          })
        }
        if (
          newlyAddedProducts.find(item => item.code === 'tl-mc-5-thryv-m2m')
        ) {
          productsToRemove.push({code: 'tl-mc-5-thryv-m2m', prodType: 'App'})
        }
      }
      fetchDependentProducts(
        newlyAddedProducts,
        allProducts,
        itemCode,
        dependentProds,
      )
      // OTF items in dependentProds have appliesTo: [{appliesProduct, ...}] (objects),
      // so the string-includes check in fetchDependentProducts always fails and every
      // newly-added OTF is incorrectly flagged as dependent. Exclude them here —
      // OTF applicability is already managed by the reconcile block below.
      productsToRemove = [
        ...productsToRemove,
        ...dependentProds.filter(item => item.prodType !== 'oneTimeFee'),
      ]
      // Always exclude keap addon parent codes from productsToRemove — they are
      // managed exclusively by the dedicated cleanup block below. The previous
      // conditional guard was broken: array passed to .includes() never matched.
      productsToRemove = productsToRemove.filter(
        item => !KEAP_ADDONS_PARENT_CODES.includes(item.code),
      )

      // Remove the newly auto added centers, if the parent center product is being removed
      const removedProdConfiguration = refProdData.find(
        item => item.code === itemCode,
      )
      if (removedProdConfiguration?.prod_type === 'Center') {
        if (
          removedProdConfiguration?.autoAddCenters &&
          removedProdConfiguration?.autoAddCenters?.length > 0
        ) {
          removedProdConfiguration?.autoAddCenters.forEach(autoCenter => {
            productsToRemove.push({code: autoCenter.code})
          })
        }
      }
      const codesToRemove = productsToRemove.map(prod => prod.code)
      if (modifiedCenters && modifiedCenters.length > 0) {
        modifiedCenters = modifiedCenters.filter(
          item => !codesToRemove.includes(item.code),
        )
      } else {
        modifiedCenters = selectCartCentersNApps.centers.filter(
          item => !codesToRemove.includes(item.code),
        )
      }
      if (modifiedApps && modifiedApps.length > 0) {
        modifiedApps = modifiedApps.filter(
          item => !codesToRemove.includes(item.code),
        )
      } else {
        modifiedApps = selectCartCentersNApps.apps.filter(
          item => !codesToRemove.includes(item.code),
        )
      }

      if (modifiedOneTimes && modifiedOneTimes.length > 0) {
        modifiedOneTimes = modifiedOneTimes.filter(
          item => !codesToRemove.includes(item.code),
        )
      } else {
        modifiedOneTimes = selectCartCentersNApps.oneTimeFees.filter(
          item => !codesToRemove.includes(item.code),
        )
      }
      // Reconcile one-time fees after removal.
      // Skip when the removed item IS an OTF — codesToRemove already handled it
      // and re-running resolveApplicableFees would incorrectly drop other OTFs
      // that are still legitimately in the cart.
      const isRemovingOTF = (selectCartCentersNApps.oneTimeFees || []).some(
        fee => fee.code === itemCode,
      )
      const oneTimeFeesCatalog = centersNAppVList.oneTimeFees.filter(
        ot => ot.appliesTo?.length > 0,
      )
      if (oneTimeFeesCatalog.length > 0 && !isRemovingOTF) {
        const existingCodesForFee = [
          ...defaultCartCentersNApps.centers.map(c => c.code),
          ...defaultCartCentersNApps.apps.map(a => a.code),
        ]
        const cartCodesForFee = [
          ...modifiedCenters.filter(c => !c.isAlreadyAdded).map(c => c.code),
          ...modifiedApps.filter(a => !a.isAlreadyAdded).map(a => a.code),
        ].filter((v, i, arr) => arr.indexOf(v) === i)
        const applicableFeeCodes = resolveApplicableFees(
          existingCodesForFee,
          cartCodesForFee,
          oneTimeFeesCatalog,
        )
        const managedCodes = new Set(oneTimeFeesCatalog.map(f => f.code))
        const retained = modifiedOneTimes.filter(
          fee =>
            !managedCodes.has(fee.code) ||
            applicableFeeCodes.includes(fee.code),
        )
        const feesToAdd = oneTimeFeesCatalog
          .filter(fee => applicableFeeCodes.includes(fee.code))
          .filter(fee => !retained.some(o => o.code === fee.code))
          .map(fee =>
            formatItem(fee, state.selectedCountry, null, 'oneTimeFees'),
          )
        modifiedOneTimes = [...retained, ...feesToAdd]
      }

      const nextCartData = {
        centers: [...modifiedCenters],
        apps: [...modifiedApps],
        oneTimeFees: [...modifiedOneTimes],
      }

      // Remove keap addon parent codes only when:
      // 1. The removed item was a keap center (not an addon or OTF)
      // 2. No keap center remains in nextCartData.centers after removal
      //    (covers both newly-added and pre-existing isAlreadyAdded centers)
      const isRemovedItemKeapCenter =
        KEAP_FAMILY_CODES.includes(itemCode) ||
        KEAP_TIER_CODES.includes(itemCode)
      const hasKeapCenterAfterRemoval = nextCartData.centers.some(
        c =>
          KEAP_FAMILY_CODES.includes(c.code) ||
          KEAP_TIER_CODES.includes(c.code),
      )
      if (isRemovedItemKeapCenter && !hasKeapCenterAfterRemoval) {
        nextCartData.apps = nextCartData.apps.filter(
          a => !KEAP_ADDONS_PARENT_NON_LEGACY_CODES.includes(a.code),
        )
        nextCartData.oneTimeFees = nextCartData.oneTimeFees.filter(
          item => !(item.code.includes('keap') && item.code.endsWith('-ot')),
        )
        Object.keys(state.cartItems).forEach(code => {
          if (state.cartItems[code]?.groupKey === 'keapadd-ons') {
            delete state.cartItems[code]
          }
        })
      }

      syncKeapTextVoiceOverride(nextCartData, defaultCartCentersNApps)

      state.selectCartCentersNApps = nextCartData

      const defaultCustomField =
        state.defaultAllGroupCustomFieldsMap[groupKey] ?? {}

      // Find all groups that depend on the group being removed
      // Only check dependencies if removing entire group (no itemCode specified)
      const dependentGroupKeys = new Set()
      if (!itemCode) {
        state.catalogGroups.forEach(catalogGroup => {
          if (catalogGroup.requires?.some(req => req.group === groupKey)) {
            dependentGroupKeys.add(catalogGroup.key)
          }
        })
      }

      // Reset custom fields for removed group (and dependent groups)
      const next = {
        ...state.allGroupCustomFieldsMap,
        [groupKey]: defaultCustomField,
      }

      dependentGroupKeys.forEach(key => {
        next[key] = state.defaultAllGroupCustomFieldsMap[key] ?? {}
      })

      state.allGroupCustomFieldsMap = next
      delete state.cartItems[itemCode]

      // Collapse all
      Object.keys(state.catalogCardCollapseStatus).forEach(key => {
        state.catalogCardCollapseStatus[key] = false
      })
      state.collapseAllTrigger += 1

      updateDisabledItemsHelper(state)
    },

    // Update group custom fields
    updateGroupCustomFields(state, action) {
      const {groupKey, updatedFields} = action.payload
      const prevGroup = state.allGroupCustomFieldsMap[groupKey] || {}
      state.allGroupCustomFieldsMap[groupKey] = {
        ...prevGroup,
        ...updatedFields,
      }
    },

    // Update catalog groups (for external updates like handleThryDesign2IfPlusSelected)
    updateCatalogGroups(state, action) {
      state.catalogGroups = action.payload
    },
    updateCatalogCollapseStatus(state, action) {
      state.catalogCardCollapseStatus[action.payload] =
        !state.catalogCardCollapseStatus[action.payload]
    },
    resetCatalogCardCollapseStatus(state) {
      state.catalogCardCollapseStatus = initialState.catalogCardCollapseStatus
    },
    updateParentCollapseStatus(state, action) {
      const {groupKey, collapse} = action.payload
      state.parentCardCollapseStatus = Object.keys(
        state.parentCardCollapseStatus,
      ).reduce(
        (acc, key) => {
          acc[key] = key === groupKey ? collapse : true
          return acc
        },
        {
          [groupKey]: collapse,
        },
      )
    },
    resetParentCardCollapseStatus(state) {
      state.parentCardCollapseStatus = initialState.parentCardCollapseStatus
    },
  },
})

export const {
  setThryvSubscriptionData,
  resetToDefaultCart,
  initializeKP11Cart,
  selectItem,
  removeItem,
  updateGroupCustomFields,
  updateCatalogGroups,
  updateCatalogCollapseStatus,
  resetCatalogCardCollapseStatus,
  updateParentCollapseStatus,
  resetParentCardCollapseStatus,
  setKeapTextVoiceQuantity,
  setKeapAddContactsQuantity,
  setSelectedKeapAddContactsOption,
  setKeapAddUsersQuantity,
  setSelectedKeapTextVoiceOption,
  setSelectedKeapKcpOption,
  setAlphaSEOFromRecurly,
  setSocialAdsBronzeQuantity,
  setSocialAdsGoldQuantity,
  setSocialAdsSilverQuantity,
  setAlphaKeyCount,
  setAlphaPowerBoostCount,
  setKeapLegacyGraphlyContactsQuantity,
  setSelectedKeapLegacyGraphlyContactsOption,
  setKeapLegacyPlusThisPremiumQuantity,
  setSelectedKeapLegacyPlusThisPremiumOption,
} = kp11CartSlice.actions

// Selectors
export const selectThryvSubscriptionResponse = state =>
  state.kp11.thryvSubscriptionResponse
export const selectDefaultCartCentersNApps = state =>
  state.kp11?.defaultCartCentersNApps
export const selectKP11CatalogGroups = state => state.kp11.catalogGroups
export const selectKP11AllGroupCustomFieldsMap = state =>
  state.kp11.allGroupCustomFieldsMap
export const selectKP11SelectedCountry = state => state.kp11.selectedCountry
export const selectKP11CentersNAppVList = state => state.kp11.centersNAppVList
export const selectKeapTextVoiceQuantity = state =>
  state.kp11.keapTextVoiceQuantity
export const selectKeapAddContactsQuantity = state =>
  state.kp11.keapAddContactsQuantity
export const selectKeapAddUsersQuantity = state =>
  state.kp11.keapAddUsersQuantity
export const selectKeapTextVoiceOption = state =>
  state.kp11.selectedKeapTextVoiceOption
export const selectedKeapKcpOption = state => state.kp11.selectedKeapKcpOption
export const selectKeapAddContactsOption = state =>
  state.kp11.selectedKeapAddContactsOption
export const selectKeapLegacyGraphlyContactsQuantity = state =>
  state.kp11.keapLegacyGraphlyContactsQuantity
export const selectKeapLegacyGraphlyContactsOption = state =>
  state.kp11.selectedKeapLegacyGraphlyContactsOption
export const selectKeapLegacyPlusThisPremiumQuantity = state =>
  state.kp11.keapLegacyPlusThisPremiumQuantity
export const selectKeapLegacyPlusThisPremiumOption = state =>
  state.kp11.selectedKeapLegacyPlusThisPremiumOption
export const selectAlphaSEOFromRecurly = state => state.kp11.alphaSEOFromRecurly
export const selectKP11DefaultCartCentersNApps = state =>
  state.kp11.defaultCartCentersNApps
export const selectKP11SelectCartCentersNApps = state =>
  state.kp11.selectCartCentersNApps
export const getCatalogCollapseStatus = state =>
  state.kp11.catalogCardCollapseStatus
export const getParentCardCollapseStatus = state =>
  state.kp11.parentCardCollapseStatus
export const selectDisabledItems = state => state.kp11.disabledItems || {}
export const selectIsItemDisabled = itemCode => state =>
  state.kp11.disabledItems?.[itemCode] || false
export const selectIsKP11DataLoaded = state => state.kp11.isDataLoaded
export const selectCollapseAllTrigger = state => state.kp11.collapseAllTrigger
export const getCartItems = state => state.kp11.cartItems
export const selectSocialAdsBronzeQuantity = state =>
  state.kp11.socialAdsBronzeQuantity
export const selectSocialAdsSilverQuantity = state =>
  state.kp11.socialAdsSilverQuantity
export const selectSocialAdsGoldQuantity = state =>
  state.kp11.socialAdsGoldQuantity
export const selectAlphaPowerBoostCount = state =>
  state.kp11.alphaPowerboostCount
export const selectAlphaKeyCount = state => state.kp11.alphaKeyCount

export default kp11CartSlice.reducer
